import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import {
  Typography,
  ListItemContainer,
  FlatListItemSeparator,
  HStack,
  Switch,
  useTheme,
  ConfirmDialog,
} from "@smartrent/ui";
import { useModalState } from "@smartrent/hooks";
import { Close } from "@smartrent/icons";

interface Entity {
  entity_id: number;
  label: string;
  enabled: boolean;
}

interface EntityListProps {
  entities: Entity[];
  title: string;
  entityType: "organization" | "group";
  onEntityClick: (entityId: number) => void;
  onRemoveEntity: (
    flagName: string,
    entityType: "organization" | "group",
    entityId: number
  ) => void;
  onEntityToggle: (
    entityType: "organization" | "group",
    entityId: number,
    enabled: boolean
  ) => void;
  flagName: string;
}

export function EntityList({
  entities,
  title,
  entityType,
  onEntityClick,
  onRemoveEntity,
  onEntityToggle,
  flagName,
}: EntityListProps) {
  const { colors } = useTheme();
  const { visible, onOpen, onClose } = useModalState();
  const [pendingToggle, setPendingToggle] = useState<{
    entityId: number;
    enabled: boolean;
  } | null>(null);

  const handleSwitchChange = (entityId: number, enabled: boolean) => {
    setPendingToggle({ entityId, enabled });
    onOpen();
  };

  const handleConfirm = () => {
    if (pendingToggle) {
      onEntityToggle(entityType, pendingToggle.entityId, pendingToggle.enabled);
    }
    setPendingToggle(null);
    onClose();
  };

  const handleCancel = () => {
    setPendingToggle(null);
    onClose();
  };

  if (entities.length === 0) {
    return null;
  }

  return (
    <View style={styles.section}>
      <Typography type="title3">{title}</Typography>
      {entities.map((entity, idx) => (
        <React.Fragment key={idx}>
          <ListItemContainer style={styles.listItem}>
            <HStack style={styles.listItemContent} spacing={8}>
              <View style={styles.labelContainer}>
                <Pressable onPress={() => onEntityClick(entity.entity_id)}>
                  <Typography type="body" style={styles.linkText}>
                    {entity.label}
                  </Typography>
                </Pressable>
              </View>
              <HStack spacing={0} style={styles.actions}>
                <Switch
                  name={`${entityType}-${entity.entity_id}`}
                  value={entity.enabled}
                  onValueChange={(enabled) =>
                    handleSwitchChange(entity.entity_id, enabled)
                  }
                />
                <Pressable
                  onPress={() =>
                    onRemoveEntity(flagName, entityType, entity.entity_id)
                  }
                >
                  <Close
                    size={20}
                    color={colors.error}
                    style={styles.deleteIcon}
                  />
                </Pressable>
              </HStack>
            </HStack>
          </ListItemContainer>
        </React.Fragment>
      ))}
      <FlatListItemSeparator style={styles.separator} />
      <ConfirmDialog
        title="Confirm Feature Flag Change"
        description={`Are you sure you want to ${
          pendingToggle?.enabled ? "enable" : "disable"
        } this feature flag?`}
        descriptionLabel={`Are you sure you want to ${
          pendingToggle?.enabled ? "enable" : "disable"
        } this feature flag?`}
        visible={visible}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        testID="entity-toggle-confirm-dialog"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginVertical: 8,
  },
  separator: {
    marginTop: 8,
  },
  listItem: {
    paddingVertical: 4,
  },
  listItemContent: {
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",
  },
  labelContainer: {
    flex: 1,
  },
  linkText: {
    textDecorationLine: "underline",
    paddingVertical: 4,
  },
  actions: {
    alignItems: "center",
    flexShrink: 0,
  },
  deleteIcon: {
    paddingLeft: 8,
    cursor: "pointer",
  },
});
